<template>
  <div class="aivai-game">
    <!-- HEADER -->
    <div class="game__header">
      <div class="is-flex is-justify-content-space-start is-align-items-center">
        <AiSelector
          color="White"
          default-engine="Stockfish"
          @onChange="onWhiteChange"
        />
      </div>

      <div
        class="is-flex is-justify-content-space-start is-align-items-center mt-4"
      >
        <AiSelector
          color="Black"
          default-engine="Lozza"
          @onChange="onBlackChange"
        />
      </div>

      <div class="is-flex is-justify-content-start is-align-items-start mt-4">
        <b-field label="Duration" label-position="on-border" class="mr-2">
          <b-select v-model="duration">
            <option value="15" key="15s">15s</option>
            <option value="60" key="1">1 minute</option>
            <option value="180" key="3">3 minutes</option>
            <option value="300" key="5">5 minutes</option>
            <option value="600" key="10">10 minutes</option>
            <option value="900" key="15">15 minutes</option>
            <option value="Unlimited" key="unlimited">Unlimited</option>
          </b-select>
        </b-field>
        <b-field
          label="Initial FEN"
          label-position="on-border"
          class="is-flex-grow-1"
        >
          <b-input
            placeholder="Input initial FEN if any"
            v-model="initialFen"
          ></b-input>
        </b-field>
      </div>

      <b-button
        :disabled="!canStartNewGame"
        class="button is-danger mt-2"
        @click="start"
      >
        START A NEW GAME
      </b-button>
      <b-button
        :disabled="canStartNewGame"
        class="button is-danger mt-2 ml-2"
        @click="stop"
      >
        STOP GAME
      </b-button>
    </div>

    <!-- GAMEBOARD -->
    <GameBoard ref="gameboard">
      <GameDataManagerLocal :matchId="matchId" />
    </GameBoard>

    <!-- REFEREE -->
    <GameRefereeLocal :matchId="matchId" />

    <!-- FOOTERS -->
    <div class="game__footer mt-6">
      <b-field
        label="FEN"
        label-position="on-border"
        type="is-info"
        class="is-flex-grow-1"
      >
        <b-input
          readonly
          :value="fen"
          icon-right="clipboard"
          icon-right-clickable
          @icon-right-click="onCopyFen()"
        />
      </b-field>

      <b-field
        label="PGN"
        label-position="on-border"
        type="is-info"
        class="is-flex-grow-1"
      >
        <b-input
          readonly
          :value="pgn"
          icon-right="clipboard"
          icon-right-clickable
          type="textarea"
          @icon-right-click="onCopyPgn()"
        />
      </b-field>
    </div>

    <GameDevInfo :gameData="gameDataCopy"></GameDevInfo>
  </div>
</template>

<script>
import AiSelector from "@/components/games/AiSelector";
import GameRefereeLocal from "@/components/games/GameRefereeLocal";
import { uuidv4 } from "vue-chess-shared/utils";
import { mapActions, mapGetters } from "vuex";
import GameBoard from "@/components/games/GameBoard";
import GameDataManagerLocal from "@/components/games/GameDataManagerLocal";
import GameDevInfo from "@/components/games/GameDevInfo";
import { GAME_RESULT } from "vue-chess-shared/constants";
import { toDateWithOffset } from "vue-chess-shared/utils/date-helper";

export default {
  name: "AivAi",

  components: {
    AiSelector,
    GameRefereeLocal,
    GameBoard,
    GameDataManagerLocal,
    GameDevInfo,
  },

  data() {
    return {
      matchId: null,
      initialFen: "",
      duration: 60,
      isPlaying: false,
    };
  },

  watch: {
    isGameOver() {
      if (!this.isGameOver) {
        return;
      }

      this.isPlaying = false;
    },

    isMatchEnded() {
      if (!this.isMatchEnded) {
        return;
      }

      this.isPlaying = false;
    },
  },

  computed: {
    ...mapGetters(["getGameDataCopyByMatchId"]),

    gameDataCopy() {
      return this.getGameDataCopyByMatchId(this.matchId);
    },

    fen() {
      return this.gameDataCopy.fen;
    },

    pgn() {
      return this.gameDataCopy.pgn;
    },

    isMatchEnded() {
      const { match } = this.gameDataCopy;
      return !!match && !!match.result;
    },

    isGameOver() {
      return this.gameDataCopy.isGameOver;
    },

    canStartNewGame() {
      return !this.isPlaying || this.isMatchEnded || this.isGameOver;
    },
  },

  methods: {
    ...mapActions(["saveLocalMatch"]),

    onWhiteChange(profile) {
      // use a temp profile so game is updated when we click start game
      this.whiteProfile = profile;
    },

    onBlackChange(profile) {
      // use a temp profile so game is updated when we click start game
      this.blackProfile = profile;
    },

    onCopyFen() {
      navigator.clipboard.writeText(this.fen).then(() => {
        this.$buefy.toast.open("FEN copied to Clipboard");
      });
    },

    onCopyPgn() {
      navigator.clipboard.writeText(this.pgn).then(() => {
        this.$buefy.toast.open("PGN copied to Clipboard");
      });
    },

    start() {
      if (this.isPlaying) {
        return;
      }

      this.isPlaying = true;
      this.initMatch();
    },

    stop() {
      this.isPlaying = false;
      this.$refs.gameboard.forceStop();
      this.saveLocalMatch({
        matchId: this.matchId,
        payload: {
          result: GAME_RESULT.ABORT,
          endedAt: toDateWithOffset(0),
        },
      });
    },

    initMatch() {
      this.matchId = uuidv4();
      this.saveLocalMatch({
        matchId: this.matchId,
        payload: {
          player1: this.whiteProfile,
          player2: this.blackProfile,
          duration: Number(this.duration),
          buffer_duration: 15,
          initial_fen: this.initialFen,
          startedAt: toDateWithOffset(0),
          createdAt: toDateWithOffset(0),
        },
      });
    },
  },
};
</script>

<style scoped>
.game__header {
  margin-bottom: 20px;
}
</style>
